import * as React from 'react';
import { createPortal } from 'react-dom';
import { AnchorRect, readAnchorRect, sameAnchorRect, useExclusiveHoverCard } from '../../hooks/useAnchorRect.ts';
import { cn } from '../../lib/utils.ts';

/** Space between the anchor and the card, and the smallest gap the card keeps from the viewport edge. */
const GAP = 4;
const VIEWPORT_MARGIN = 8;

export interface HoverCardProps {
  anchor: AnchorRect;
  className?: string;
  children: React.ReactNode;
}

/**
 * A hover card placed beside its anchor, fixed-positioned in a body portal.
 *
 * The portal and the fixed position are the point of this component, not decoration. An in-flow absolute
 * card hanging past the bottom of the page adds to the scroll container's scrollable height; when the reader
 * is already scrolled to the bottom that growth shifts the content out from under the pointer, which drops
 * the hover, which removes the card, which shrinks the page back - an endless bounce. Fixed elements
 * contribute no scrollable overflow, so there is nothing left to grow.
 *
 * It also never intercepts the pointer: the card hangs over whatever is below it, and the controls under it
 * have to stay clickable on the way past.
 */
export function HoverCard({ anchor, className, children }: HoverCardProps) {
  const cardRef = React.useRef<HTMLDivElement>(null);
  // Hidden for the measuring pass only. The real placement is set in a layout effect, so it lands before the
  // browser paints and the card is never seen in the wrong spot.
  const [style, setStyle] = React.useState<React.CSSProperties>({ visibility: 'hidden', top: 0, left: 0 });

  React.useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const { width, height } = card.getBoundingClientRect();
    const roomBelow = window.innerHeight - anchor.bottom - GAP - VIEWPORT_MARGIN;
    // Flipping up only helps if the card actually fits up there. Otherwise it stays below and is nudged up
    // just enough to stay on screen, which still reads top-down.
    const flipUp = height > roomBelow && height + GAP + VIEWPORT_MARGIN <= anchor.top;

    setStyle({
      top: flipUp
        ? anchor.top - GAP - height
        : Math.max(VIEWPORT_MARGIN, Math.min(anchor.bottom + GAP, window.innerHeight - height - VIEWPORT_MARGIN)),
      left: Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, window.innerWidth - width - VIEWPORT_MARGIN))
    });
  }, [anchor, children]);

  return createPortal(
    <div
      ref={cardRef}
      role="tooltip"
      style={style}
      className={cn(
        'pointer-events-none fixed z-[1200] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md',
        className
      )}
    >
      {children}
    </div>,
    document.body
  );
}

/**
 * The same card, for use as a recharts `<Tooltip content={...}>`.
 *
 * Recharts positions its tooltip wrapper absolutely inside the chart and only clamps it to the plot's
 * viewBox with `Math.max(negative, viewBox[key])`. When the card is taller than the plot area - routine in
 * the short cards here - that clamp collapses and the wrapper runs past the chart, which is the bounce
 * described above. Nothing on the path clips it: ChartContainer, Card and CardContent are all
 * overflow-visible.
 *
 * So the card leaves a 1px probe behind in the wrapper and renders itself out of flow instead. The probe is
 * only there to be measured: recharts has already moved the wrapper to the cursor, so the probe's rect is
 * where the card belongs, with no chart-relative coordinate maths to keep in sync.
 *
 * The probe has to have a size. Recharts only writes a transform onto the wrapper when it measures the
 * wrapper as both wider and taller than zero (`getTooltipTranslate`); a zero-sized one is left at the plot's
 * top-left with `visibility: hidden` - which the portaled card does not inherit, so the card would be
 * perfectly visible, parked at the start of the chart, for every bar. 1px is enough to be measured and small
 * enough that recharts' own offset maths stays effectively cursor-relative; the real edge handling is
 * HoverCard's viewport clamp, which knows the card's true size.
 *
 * Call sites must also pass `isAnimationActive={false}` to the recharts `<Tooltip>`. Its default animation
 * puts a transform transition on the wrapper, so the probe's rect during a move is wherever the animation
 * currently is rather than where the cursor is, and the card trails a bar or two behind.
 *
 * Dismissing is this component's own business too. Recharts decides whether the tooltip is active, from the
 * same mouse events that go missing when a chart scrolls out from under a stationary cursor - so it can sit
 * there believing it is still hovered. Dismissal therefore means "render nothing until recharts moves the
 * tooltip again", which is the one signal that proves the pointer is really back.
 */
export function ChartHoverCard({ className, children }: { className?: string; children: React.ReactNode }) {
  const probeRef = React.useRef<HTMLSpanElement>(null);
  const [anchor, setAnchor] = React.useState<AnchorRect | null>(null);
  const anchorRef = React.useRef<AnchorRect | null>(null);
  /** Where the card was when it was dismissed, so an unmoved tooltip is not immediately reopened. */
  const dismissedAt = React.useRef<AnchorRect | null>(null);

  anchorRef.current = anchor;

  const dismiss = React.useCallback(() => {
    dismissedAt.current = anchorRef.current;
    setAnchor(null);
  }, []);

  const announceOpen = useExclusiveHoverCard(anchor !== null, dismiss);

  React.useEffect(() => {
    if (!anchor) return;

    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);

    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [anchor, dismiss]);

  // Deliberately no dependency list: recharts re-renders this content with a fresh wrapper transform on
  // every pointer move, and re-measuring is the only way to follow it. The equality guard is what keeps
  // setting state here from looping.
  React.useLayoutEffect(() => {
    const probe = probeRef.current;
    if (!probe) return;

    const rect = readAnchorRect(probe);

    if (dismissedAt.current) {
      if (sameAnchorRect(dismissedAt.current, rect)) return;
      dismissedAt.current = null;
    }

    if (anchorRef.current === null) announceOpen();
    setAnchor((previous) => (sameAnchorRect(previous, rect) ? previous : rect));
  });

  return (
    <>
      <span ref={probeRef} aria-hidden="true" className="block h-px w-px" />
      {anchor && (
        <HoverCard anchor={anchor} className={className}>
          {children}
        </HoverCard>
      )}
    </>
  );
}
